import { useEffect } from "react";
import { cn } from "tailwind-variants";
import { submitDtfEvent } from "common/utils/mixpanel/submit-dtf-events";
import { isProduction } from "common/utils/env";
import { readCookie } from "common/utils/cookie";
import { api } from "jiffy-api";
import { useDtfStore } from "../store";
import "./GangSheetBuilderButton.scss";

const INCHES_TO_FEET = 12;

export default function GangSheetBuilderButton({ ...restProps }) {
  const dtfStore = useDtfStore();

  function gangSheetBuilderSettings() {
    return dtfStore.gangSheetBuilderSettings;
  }

  function gangSheetBuilder() {
    return dtfStore.gangSheetBuilder;
  }

  function gangSheetBuilderShopId() {
    return dtfStore.gangSheetBuilderShopId;
  }

  function gangSheetBuilderVariants() {
    return dtfStore.gangSheetBuilderVariants;
  }

  function gangSheetBuilderSizes() {
    return dtfStore.gangSheetBuilderSizes;
  }

  function gangSheetBulkPrice() {
    return dtfStore.gangSheetBulkPrice;
  }

  function preloadedDesignId() {
    return dtfStore.preloadedDesignId;
  }

  function catalogNumber() {
    return dtfStore.catalogNumber;
  }

  function gangSheetBuilderSizesAndVariants() {
    const gangSheetBuilderSizesAndVariants = []; // eslint-disable-line no-shadow
    let sizeId = 1;
    Object.entries(gangSheetBuilderVariants()).forEach(([label, variant]) => {
      gangSheetBuilderSizes().forEach(size => {
        gangSheetBuilderSizesAndVariants.push({
          ...size,
          title: `${label} - ${size.height / INCHES_TO_FEET} Feet`,
          metadata: {
            variantId: variant.variant_id
          },
          id: sizeId
        });
        sizeId += 1;
      });
    });
    return gangSheetBuilderSizesAndVariants;
  }

  function selectedSize() {
    return gangSheetBuilderSizesAndVariants().find(
      size =>
        size.height === gangSheetBuilderSettings().height &&
        size.metadata.variantId === gangSheetBuilderSettings().variantId
    );
  }

  function builderPayload() {
    const payload = {
      container: "gs-builder",
      mode: isProduction ? "production" : "development",
      shop_id: gangSheetBuilderShopId(),
      size_id: selectedSize().id,
      sizes: gangSheetBuilderSizesAndVariants(),
      settings: {
        nameAndNumber: {
          enabled: true
        }
      }
    };
    const customerId = readCookie("gangsheet_builder_customer_id");
    if (customerId) {
      payload.customer = {
        id: customerId
      };
    }
    return payload;
  }

  function setLoadedArtwork(...args) {
    return dtfStore.setLoadedArtwork(...args);
  }

  function setPreloadedDesignId(...args) {
    return dtfStore.setPreloadedDesignId(...args);
  }

  function submitForm(...args) {
    return dtfStore.submitForm(...args);
  }

  function showBuilder() {
    submitDtfEvent("gangsheet_builder_opened", {
      size: Math.ceil(gangSheetBuilderSettings().height / 12.0),
      variant: gangSheetBuilderSettings().variantId
    });
    gangSheetBuilder()
      .open(builderPayload())
      .on("design:created", async gangSheetData => {
        const { designs } = gangSheetData.data;
        await addToCartGangSheet(designs);
      })
      .on("closed", () => {});
  }

  function editGangSheet() {
    gangSheetBuilder()
      .open({
        ...builderPayload(),
        shop_id: gangSheetBuilderShopId(),
        design_id: preloadedDesignId()
      })
      .on("design:updated", async gangSheetData => {
        const { designs } = gangSheetData.data;
        designs.forEach(design => {
          if (!design.size.metadata)
            design.size.metadata = {
              variantId: gangSheetBuilderSettings().variantId
            };
        });
        await addToCartGangSheet(designs);
      });
  }

  async function addToCartGangSheet(designs) {
    const response = await api.dtf.addToCartGangSheet({
      gang_sheet_data: designs,
      catalog_number: catalogNumber()
    });
    gangSheetBuilder().close();
    response.artworks.forEach((artwork, index) => {
      const bulkPrice = gangSheetBulkPrice()[artwork.variant_id]?.bulk_price;
      let gangsheetPrice = bulkPrice?.find(
        size => size.height === artwork.height * artwork.quantity
      );
      const selectedVariant = Object.values(gangSheetBuilderVariants()).find(
        variant => variant.variant_id === artwork.variant_id
      );
      gangsheetPrice ||= bulkPrice[bulkPrice.length - 1];
      artwork.pricing = {
        unitPrice: gangsheetPrice.size_price * Math.ceil(artwork.height / 12.0)
      };
      artwork.catalogNumber = selectedVariant.catalog_number;
      artwork.fromGangSheetBuilder = true;
      setLoadedArtwork({
        artwork,
        index
      });
    });
    submitForm();
  }

  useEffect(() => {
    if (preloadedDesignId()) {
      editGangSheet();
      setPreloadedDesignId(null);
    }
  }, []);

  return (
    <div {...restProps} className={cn("gang-sheet-builder-button", restProps.className)}>
      <button
        className="gang-sheet-builder-button__button"
        type="button"
        onClick={e => {
          e.stopPropagation();
          showBuilder();
        }}
        data-testid="gang-sheet-builder-button-button"
      >
        {" "}
        Build Your Gang Sheet{" "}
      </button>
    </div>
  );
}
